import {
  Activity,
  Building2,
  CalendarDays,
  Camera,
  ChevronDown,
  CheckSquare,
  ClipboardCheck,
  Construction,
  CreditCard,
  DollarSign,
  FileText,
  HardHat,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  Package,
  ShieldCheck,
  Sun,
  Target,
  Users
} from "lucide-react";
import { useState } from "react";
import { NavLink } from "react-router-dom";
import { toast } from "sonner";

import NotificationBell from "../components/shared/NotificationBell";
import ProjectFilter from "../components/shared/ProjectFilter";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Input } from "../components/ui/input";
import { Sheet, SheetContent, SheetHeader } from "../components/ui/sheet";
import { useApi } from "../context/ApiContext";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { useTheme } from "../context/ThemeContext";
import { useProjectFilter } from "../hooks/useProjectFilter";

const Navigation = ({ sidebarOpen, setSidebarOpen }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { projects } = useData();
  const { projectId, updateProject } = useProjectFilter();
  const api = useApi();
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const handlePasswordUpdate = async () => {
    if (!newPassword.trim() || !confirmPassword.trim()) {
      toast.error("Please fill in all password fields");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (!user?.id) {
      toast.error("Unable to identify current user");
      return;
    }

    setIsUpdatingPassword(true);
    try {
      await api.put(`/users/${user.id}/password`, { newPassword });
      toast.success("Password updated successfully!");
      setIsPasswordDialogOpen(false);
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.message || "Failed to update password");
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  // Helper to append current projectId to navigation paths
  const getLinkTo = (path) => {
    return path;
  };

  // Role-based navigation items
  const getNavItemsForRole = (userRole) => {
    const allItems = [
      {
        id: "dashboard",
        label: "Dashboard",
        path: "/",
        icon: LayoutDashboard,
        category: "project"
      },
      {
        id: "project",
        label: "Projects",
        path: "/projects",
        icon: Construction,
        category: "global"
      },
      {
        id: "task",
        label: "Tasks",
        path: "/tasks",
        icon: CheckSquare,
        category: "project"
      },
      {
        id: "activity",
        label: "Activities",
        path: "/activities",
        icon: Activity,
        category: "project"
      },
      {
        id: "material",
        label: "Materials",
        path: "/materials",
        icon: Package,
        category: "project"
      },
      {
        id: "attendance",
        label: "Attendance",
        path: "/attendance",
        icon: ClipboardCheck,
        category: "global"
      },
      {
        id: "document",
        label: "Documents",
        path: "/documents",
        icon: Camera,
        category: "project"
      },
      {
        id: "vendor",
        label: "Vendors",
        path: "/vendors",
        icon: Users,
        category: "global"
      },
      {
        id: "invoice",
        label: "Invoices",
        path: "/invoices",
        icon: FileText,
        category: "project"
      },
      {
        id: "payment",
        label: "Payments",
        path: "/payments",
        icon: CreditCard,
        category: "project"
      },
      {
        id: "contractor",
        label: "Contractors",
        path: "/contractors",
        icon: HardHat,
        category: "global"
      },
      {
        id: "costing",
        label: "Costing",
        path: "/contractor-costing",
        icon: DollarSign,
        category: "project"
      },
      {
        id: "meeting",
        label: "Meetings",
        path: "/meetings",
        icon: CalendarDays,
        category: "global"
      },
      {
        id: "lead",
        label: "Lead Manager",
        path: "/leads",
        icon: Target,
        category: "global"
      },
      {
        id: "user",
        label: "User Management",
        path: "/users",
        icon: Users,
        category: "global"
      },
      {
        id: "role",
        label: "Role Management",
        path: "/roles",
        icon: ShieldCheck,
        category: "global"
      },
      {
        id: "tenant",
        label: "Tenants",
        path: "/tenants",
        icon: Building2,
        category: "global"
      }
    ];

    const permissions = user?.permissions || {};
    const roleName = userRole?.name || userRole;

    const filtered = allItems.filter((item) => {
      if (roleName === "super_admin") return true;
      if (item.id === "tenant") return false; // Only Super Admin sees Tenants
      if (roleName === "admin") return true;
      const isAdminModule = item.id === "user" || item.id === "role";
      if (isAdminModule) {
        const p = permissions[item.id];
        return p?.read === true && (p?.create === true || p?.update === true || p?.delete === true);
      }

      return permissions[item.id]?.read === true;
    });

    return {
      projectItems: filtered.filter((i) => i.category === "project"),
      globalItems: filtered.filter((i) => i.category === "global")
    };
  };

  const navItems = getNavItemsForRole(user?.role);

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0 border-r border-border z-20 bg-card dark:bg-yale-blue-900 shadow-xl">
        <div className="flex flex-col h-full">
          <div className="bg-card/95 dark:bg-yale-blue-900/95 backdrop-blur-sm z-10 shrink-0 border-b border-border dark:border-yale-blue-800">
            <div className="flex items-center px-6 pt-6 pb-2">
              <NavLink to="/" className="block">
                <img
                  src="/images/brikflow_logo_login-removebg-preview.png"
                  alt="BrikFlow Logo"
                  className="w-[250px] object-contain"
                />
              </NavLink>
            </div>
          </div>

          <nav className="flex-1 px-4 py-6 space-y-8 overflow-y-auto custom-scrollbar">
            {/* Project Section */}
            {navItems.projectItems.length > 0 && (
              <div className="space-y-2">
                <p className="px-2 text-[10px] font-bold text-muted-foreground/60 uppercase tracking-widest mb-3">Project</p>

                <div className="mb-4">
                  <ProjectFilter
                    selectedProject={projectId === "ALL" ? "" : projectId}
                    onProjectChange={updateProject}
                    plain={true}
                    fullWidth={true}
                    showLabel={false}
                  />
                </div>

                {navItems.projectItems.map((item, index) => (
                  <NavLink
                    key={item.id}
                    to={getLinkTo(item.path)}
                    className={({ isActive }) =>
                      `w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-300 transform animate-in fade-in slide-in-from-left-4 fill-mode-both group ${
                        isActive
                          ? "bg-gradient-to-r from-primary/90 to-primary text-white shadow-md shadow-primary/10"
                          : "text-muted-foreground hover:bg-accent dark:hover:bg-yale-blue-800/50 hover:text-foreground"
                      }`
                    }
                    style={{
                      animationDelay: `${index * 40}ms`,
                      animationDuration: "500ms"
                    }}
                  >
                    {({ isActive }) => (
                      <>
                        <item.icon
                          className={`h-[18px] w-[18px] transition-colors duration-300 ${
                            isActive
                              ? "text-white"
                              : "text-primary/60 group-hover:text-primary dark:text-primary/80 dark:group-hover:text-white"
                          }`}
                        />
                        <span className="tracking-tight">{item.label}</span>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            )}

            {/* Management Section */}
            {navItems.globalItems.length > 0 && (
              <div className="space-y-2">
                <p className="px-4 text-[10px] font-bold text-muted-foreground/60 uppercase tracking-widest mb-3">Management</p>
                {navItems.globalItems.map((item, index) => (
                  <NavLink
                    key={item.id}
                    to={getLinkTo(item.path)}
                    className={({ isActive }) =>
                      `w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-all duration-300 transform animate-in fade-in slide-in-from-left-4 fill-mode-both group ${
                        isActive
                          ? "bg-gradient-to-r from-primary to-yale-blue-600 text-white shadow-md shadow-primary/20 scale-[1.02] translate-x-1"
                          : "text-muted-foreground hover:bg-accent dark:hover:bg-yale-blue-800/50 hover:text-foreground"
                      }`
                    }
                    style={{
                      animationDelay: `${(navItems.projectItems.length + index) * 40}ms`,
                      animationDuration: "500ms"
                    }}
                  >
                    {({ isActive }) => (
                      <>
                        <item.icon
                          className={`h-4 w-4 transition-colors duration-300 ${
                            isActive
                              ? "text-white"
                              : "text-primary/60 group-hover:text-primary dark:text-primary/80 dark:group-hover:text-white"
                          }`}
                        />
                        <span className="tracking-tight">{item.label}</span>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            )}
          </nav>
        </div>
      </aside>

      {/* Mobile Navigation Header */}
      <div className="lg:hidden bg-background/80 dark:bg-yale-blue-900/80 shadow-sm border-b border-border dark:border-yale-blue-800 px-4 py-3 flex items-center justify-between sticky top-0 z-[100] backdrop-blur-md w-full">
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            className="mr-2 text-foreground hover:bg-accent dark:hover:bg-yale-blue-800"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <NavLink to="/" onClick={() => setSidebarOpen(false)} className="block">
            <img src="/images/brikflow_logo_login-removebg-preview.png" alt="BrikFlow Logo" className="h-7 w-auto object-contain" />
          </NavLink>
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell showZero={false} />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-9 px-2 rounded-md flex items-center gap-1 hover:bg-slate-100 dark:hover:bg-yale-blue-800"
                aria-label="Profile menu"
                title={user?.name || "Profile"}
              >
                <Avatar className="h-7 w-7">
                  <AvatarImage src={user?.avatarUrl} />
                  <AvatarFallback className="bg-primary text-white text-[10px] font-bold">{getInitials(user?.name)}</AvatarFallback>
                </Avatar>
                <ChevronDown className="h-3.5 w-3.5 text-slate-500 dark:text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={toggleTheme} className="cursor-pointer whitespace-nowrap">
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                {theme === "dark" ? "Switch to Light Theme" : "Switch to Dark Theme"}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setIsPasswordDialogOpen(true)}
                className="cursor-pointer whitespace-nowrap"
              >
                <KeyRound className="h-4 w-4" />
                Change Password
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={logout}
                className="cursor-pointer font-semibold uppercase tracking-wider text-red-600 dark:text-red-400"
              >
                <LogOut className="h-4 w-4" />
                LOGOUT
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent
          side="left"
          className="w-[280px] sm:w-64 p-0 mobile-sidebar border-r border-border dark:border-yale-blue-800"
        >
          <div className="flex flex-col h-full bg-card">
            <SheetHeader className="px-6 pt-6 pb-4 border-b border-border dark:border-yale-blue-800 bg-card/50 backdrop-blur-sm">
              <div className="flex items-center mb-2">
                <NavLink to="/" onClick={() => setSidebarOpen(false)} className="block">
                  <img
                    src="/images/brikflow_logo_login-removebg-preview.png"
                    alt="BrikFlow Logo"
                    className="h-8 w-auto object-contain"
                  />
                </NavLink>
              </div>
            </SheetHeader>
            <nav className="flex-1 px-4 py-4 space-y-8 overflow-y-auto custom-scrollbar">
              {/* Project Section */}
              {navItems.projectItems.length > 0 && (
                <div className="space-y-2">
                  <p className="px-2 text-[10px] font-bold text-muted-foreground/60 uppercase tracking-widest mb-3">Project</p>

                  <div className="mb-4">
                    <ProjectFilter
                      selectedProject={projectId === "ALL" ? "" : projectId}
                      onProjectChange={updateProject}
                      plain={true}
                      fullWidth={true}
                      showLabel={false}
                    />
                  </div>

                  {navItems.projectItems.map((item, index) => (
                    <NavLink
                      key={item.id}
                      to={getLinkTo(item.path)}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-300 transform animate-in fade-in slide-in-from-left-4 fill-mode-both group ${
                          isActive
                            ? "bg-gradient-to-r from-primary/90 to-primary text-white shadow-md shadow-primary/10"
                            : "text-muted-foreground hover:bg-accent dark:hover:bg-yale-blue-800/50 hover:text-foreground"
                        }`
                      }
                      style={{
                        animationDelay: `${index * 40}ms`,
                        animationDuration: "500ms"
                      }}
                    >
                      {({ isActive }) => (
                        <>
                          <item.icon
                            className={`h-4 w-4 transition-colors duration-300 ${
                              isActive
                                ? "text-white"
                                : "text-primary/60 group-hover:text-primary dark:text-primary/80 dark:group-hover:text-white"
                            }`}
                          />
                          <span className="tracking-tight">{item.label}</span>
                        </>
                      )}
                    </NavLink>
                  ))}
                </div>
              )}

              {/* Management Section */}
              {navItems.globalItems.length > 0 && (
                <div className="space-y-3">
                  <p className="px-2 text-[10px] font-bold text-muted-foreground/60 uppercase tracking-widest mb-3">Management</p>
                  {navItems.globalItems.map((item, index) => (
                    <NavLink
                      key={item.id}
                      to={getLinkTo(item.path)}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-300 transform animate-in fade-in slide-in-from-left-4 fill-mode-both group ${
                          isActive
                            ? "bg-gradient-to-r from-primary/90 to-primary text-white shadow-md shadow-primary/10"
                            : "text-muted-foreground hover:bg-accent dark:hover:bg-yale-blue-800/50 hover:text-foreground"
                        }`
                      }
                      style={{
                        animationDelay: `${(navItems.projectItems.length + index) * 40}ms`,
                        animationDuration: "500ms"
                      }}
                    >
                      {({ isActive }) => (
                        <>
                          <item.icon
                            className={`h-[18px] w-[18px] transition-colors duration-300 ${
                              isActive
                                ? "text-white"
                                : "text-primary/60 group-hover:text-primary dark:text-primary/80 dark:group-hover:text-white"
                            }`}
                          />
                          <span className="tracking-tight">{item.label}</span>
                        </>
                      )}
                    </NavLink>
                  ))}
                </div>
              )}
            </nav>
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>Enter and confirm your new password.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <Input
              type="password"
              placeholder="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <Input
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsPasswordDialogOpen(false);
                setNewPassword("");
                setConfirmPassword("");
              }}
            >
              Cancel
            </Button>
            <Button onClick={handlePasswordUpdate} disabled={isUpdatingPassword}>
              {isUpdatingPassword ? "Updating..." : "Update Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Navigation;
